/**
 * PlayerPreviewSectionView — one titled block inside the player preview drawer
 * (SE-5494, restyled SE-6554).
 *
 * Every drawer section (key info, ratings, reports, season form, tags,
 * activity) shares this chrome so the vertical rhythm is defined once. Spacing
 * is owned here rather than by a parent `Stack`'s `space`, because the restyle
 * separates sections with a RULE: the divider and the padding either side of it
 * are one decision, and splitting them across two components is how a rule ends
 * up sitting off-centre in its own gap.
 *
 * The divider is a TOP border on every section but the first. Drawing it on the
 * top rather than the bottom means the last section never trails a rule into
 * the footer's own border, and the leading section never opens with one.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/PlayerPreviewSection
 */

import { memo, type ReactNode } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link PlayerPreviewSectionView}. */
export interface PlayerPreviewSectionViewProps {
  /** Section heading. */
  readonly title: string;
  /** Section body. */
  readonly children: ReactNode;
  /**
   * Rendered at the trailing edge of the heading row — the pinned note's
   * recency badge, a counter, a link. Omit for a plain heading.
   */
  readonly headerAccessory?: ReactNode;
  /**
   * Whether this is the FIRST section under the hero, which opens without a
   * separating rule.
   */
  readonly isLeading?: boolean;
  /** Test/a11y identity for the section root. */
  readonly testID?: string;
}

/**
 * Renders a titled drawer section.
 * @param props - {@link PlayerPreviewSectionViewProps}
 * @param props.title - Section heading
 * @param props.children - Section body
 * @param props.headerAccessory - Trailing node in the heading row
 * @param props.isLeading - Whether the separating rule is suppressed
 * @param props.testID - Test identity for the section root
 * @returns The section.
 */
const PlayerPreviewSectionView = ({
  title,
  children,
  headerAccessory,
  isLeading = false,
  testID,
}: PlayerPreviewSectionViewProps) => (
  <Stack
    space="3"
    className={
      isLeading ? "pb-4 pt-3" : "border-t border-outline-default pb-4 pt-3"
    }
    testID={testID}
  >
    <Stack
      direction="horizontal"
      className="min-h-5 items-center justify-between gap-2"
    >
      {/* The eyebrow: the smallest rung on the type scale, tracked out and in
          the heading face, so a section label reads as chrome rather than as
          content competing with the values beneath it. */}
      <Text
        variant="micro-strong"
        className="uppercase tracking-widest text-content-muted"
      >
        {title}
      </Text>
      {headerAccessory}
    </Stack>
    {children}
  </Stack>
);

PlayerPreviewSectionView.displayName = "PlayerPreviewSectionView";

export default memo(PlayerPreviewSectionView);
